import { useState, useMemo, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { currentUserId } from '@/lib/currentUser';
import { ImportContractsDialog } from '@/components/contracts/ImportContractsDialog';
import { RentLinesEditor, buildLeaseAmounts, draftsFromLease, type RentLineDraft } from '@/components/contracts/RentLinesEditor';
import { IndexationSettings, indexationFieldsFromForm } from '@/components/contracts/IndexationSettings';
import { PaymentAccountSelect } from '@/components/contracts/PaymentAccountSelect';
import { AppLayout } from '@/components/layout/AppLayout';
import { PageHeader } from '@/components/ui/page-header';
import { DataTable } from '@/components/ui/data-table';
import { StatusBadge } from '@/components/ui/status-badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Plus, Euro, Calendar, Percent, MoreHorizontal, Pause, Play, XCircle, Filter, X, Trash2, Edit, Eye, Home, Search, Loader2 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { Switch } from '@/components/ui/switch';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Contracts() {
  const [isOpen, setIsOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [selectedContract, setSelectedContract] = useState<any>(null);
  const [editingContract, setEditingContract] = useState<any>(null);
  const [selectedPropertyId, setSelectedPropertyId] = useState<string>('');
  const [paymentAccountId, setPaymentAccountId] = useState('');
  const [filterSciId, setFilterSciId] = useState<string>('all');
  const [filterPropertyId, setFilterPropertyId] = useState<string>('all');
  const [filterTenantId, setFilterTenantId] = useState<string>('all');
  // Billing frequency state
  const [billingFrequency, setBillingFrequency] = useState<'monthly' | 'quarterly'>('monthly');
  const [quarterlyMonths, setQuarterlyMonths] = useState<number[]>([1, 4, 7, 10]);
  // Edit form state
  const [editBillingFrequency, setEditBillingFrequency] = useState<'monthly' | 'quarterly'>('monthly');
  const [editQuarterlyMonths, setEditQuarterlyMonths] = useState<number[]>([1, 4, 7, 10]);
  // Extra rent lines (each with its own TVA)
  const [firstLineLabel, setFirstLineLabel] = useState('');
  const [extraLines, setExtraLines] = useState<RentLineDraft[]>([]);
  const [editFirstLineLabel, setEditFirstLineLabel] = useState('');
  const [editExtraLines, setEditExtraLines] = useState<RentLineDraft[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const queryClient = useQueryClient();

  // Month names in French
  const monthNames = [
    'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
    'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
  ];

  // Fetch SCIs for filter
  const { data: scis = [] } = useQuery({
    queryKey: ['scis-list'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('scis')
        .select('id, name')
        .order('name');
      if (error) throw error;
      return data;
    },
  });

  const { data: contracts = [], isLoading } = useQuery({
    queryKey: ['leases'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('leases')
        .select('*, sci_payment_accounts!leases_payment_account_id_fkey(id, label, iban), properties(id, name, address, sci_id, scis(id, name)), tenants(id, first_name, last_name, tenant_type, company_name)')
        .order('created_at', { ascending: false });
      if (error) throw error;
      return data;
    },
  });

  const { data: properties = [] } = useQuery({
    queryKey: ['properties-with-sci'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('properties')
        .select('id, name, address, vat_rate, scis(id, name)');
      if (error) throw error;
      return data;
    },
  });

  const { data: tenants = [] } = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenants')
        .select('id, first_name, last_name, tenant_type, company_name');
      if (error) throw error;
      return data;
    },
  });

  // Filter contracts
  const filteredContracts = useMemo(() => {
    const term = searchTerm.toLowerCase().trim();
    return contracts.filter((contract: any) => {
      // Filter by SCI
      const sciId = contract.properties?.scis?.id;
      if (filterSciId !== 'all' && sciId !== filterSciId) return false;
      
      // Filter by Property
      const propertyId = contract.properties?.id;
      if (filterPropertyId !== 'all' && propertyId !== filterPropertyId) return false;
      
      // Filter by Tenant
      const tenantId = contract.tenants?.id;
      if (filterTenantId !== 'all' && tenantId !== filterTenantId) return false;

      // Search filter
      if (term) {
        const propertyName = (contract.properties?.name || contract.properties?.address || '').toLowerCase();
        const sciName = (contract.properties?.scis?.name || '').toLowerCase();
        const tenantName = `${contract.tenants?.first_name || ''} ${contract.tenants?.last_name || ''} ${contract.tenants?.company_name || ''}`.toLowerCase();
        if (!propertyName.includes(term) && !sciName.includes(term) && !tenantName.includes(term)) {
          return false;
        }
      }
      
      return true;
    });
  }, [contracts, filterSciId, filterPropertyId, filterTenantId, searchTerm]);

  const hasActiveFilters = filterSciId !== 'all' || filterPropertyId !== 'all' || filterTenantId !== 'all' || searchTerm;

  const selectedProperty = properties.find(p => p.id === selectedPropertyId);

  useEffect(() => {
    const drafts = draftsFromLease(editingContract);
    setEditFirstLineLabel(drafts.firstLabel);
    setEditExtraLines(drafts.extras);
  }, [editingContract]);

  const editFirstLine = Array.isArray(editingContract?.rent_lines) && editingContract.rent_lines.length > 0
    ? editingContract.rent_lines[0]
    : null;

  const createContract = useMutation({
    mutationFn: async (formData: FormData) => {
      const { data: profile } = await supabase
        .from('profiles')
        .select('organization_id')
        .eq('user_id', await currentUserId())
        .single();

      if (!profile?.organization_id) throw new Error('No organization');

      const { rent_amount: rentAmount, charges_amount: chargesAmount, vat_rate: vatRate, rent_lines } =
        buildLeaseAmounts(formData, firstLineLabel, extraLines);
      const depositAmount = parseFloat(formData.get('deposit_amount') as string) || 0;
      const includeDepositInFirstCall = formData.get('include_deposit_in_first_call') === 'on';
      const autoGenerate = formData.get('auto_generate') === 'on';

      const { data, error } = await supabase
        .from('leases')
        .insert({
        organization_id: profile.organization_id,
        property_id: formData.get('property_id') as string,
        payment_account_id: paymentAccountId || null,
        tenant_id: formData.get('tenant_id') as string,
        start_date: formData.get('start_date') as string,
        rent_amount: rentAmount,
        charges_amount: chargesAmount,
        rent_lines: rent_lines as any,
        deposit_amount: depositAmount,
        include_deposit_in_first_call: includeDepositInFirstCall,
        payment_day: parseInt(formData.get('payment_day') as string) || 5,
        vat_rate: vatRate,
        status: 'active',
        auto_generate: autoGenerate,
        billing_frequency: billingFrequency,
        quarterly_months: quarterlyMonths,
        })
        .select()
        .single();
      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leases'] });
      setIsOpen(false);
      setSelectedPropertyId('');
      setPaymentAccountId('');
      setBillingFrequency('monthly');
      setQuarterlyMonths([1, 4, 7, 10]);
      setFirstLineLabel('');
      setExtraLines([]);
      toast.success('Contrat créé avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const updateContract = useMutation({
    mutationFn: async (formData: FormData) => {
      if (!editingContract?.id) throw new Error('No contract selected');

      const { rent_amount: rentAmount, charges_amount: chargesAmount, vat_rate: vatRate, rent_lines } =
        buildLeaseAmounts(formData, editFirstLineLabel, editExtraLines);
      const depositAmount = parseFloat(formData.get('deposit_amount') as string) || 0;
      const includeDepositInFirstCall = formData.get('include_deposit_in_first_call') === 'on';
      const autoGenerate = formData.get('auto_generate') === 'on';

      const { error } = await supabase
        .from('leases')
        .update({
          property_id: formData.get('property_id') as string,
          payment_account_id: paymentAccountId || null,
          tenant_id: formData.get('tenant_id') as string,
          start_date: formData.get('start_date') as string,
          end_date: formData.get('end_date') as string || null,
          rent_amount: rentAmount,
          charges_amount: chargesAmount,
          rent_lines: rent_lines as any,
          deposit_amount: depositAmount,
          include_deposit_in_first_call: includeDepositInFirstCall,
          payment_day: parseInt(formData.get('payment_day') as string) || 5,
          vat_rate: vatRate,
          auto_generate: autoGenerate,
          billing_frequency: editBillingFrequency,
          quarterly_months: editQuarterlyMonths,
          ...indexationFieldsFromForm(formData),
        })
        .eq('id', editingContract.id);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leases'] });
      setEditingContract(null);
      setIsEditOpen(false);
      setSelectedContract(null);
      setSelectedPropertyId('');
      setPaymentAccountId('');
      toast.success('Contrat mis à jour avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const updateContractStatus = useMutation({
    mutationFn: async ({ id, status, endDate }: { id: string; status: 'active' | 'paused' | 'ended'; endDate?: string }) => {
      const updateData: any = { status };
      if (endDate) {
        updateData.end_date = endDate;
      }
      const { error } = await supabase
        .from('leases')
        .update(updateData)
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['leases'] });
      const messages: Record<string, string> = {
        active: 'Contrat réactivé',
        paused: 'Contrat suspendu',
        ended: 'Contrat terminé',
      };
      toast.success(messages[variables.status]);
    },
    onError: (error: any) => {
      toast.error(error.message);
    },
  });

  const deleteContract = useMutation({
    mutationFn: async (contractId: string) => {
      const { error } = await supabase
        .from('leases')
        .delete()
        .eq('id', contractId);
      
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['leases'] });
      toast.success('Contrat supprimé avec succès');
    },
    onError: (error: any) => {
      toast.error(error.message || 'Erreur lors de la suppression');
    },
  });

  const columns = [
    {
      header: 'Bien',
      accessorKey: (row: any) => (
        <div>
          <div className="font-medium">{row.properties?.name || row.properties?.address}</div>
          <div className="text-xs text-muted-foreground">{row.properties?.scis?.name}</div>
        </div>
      ),
    },
    {
      header: 'Locataire',
      accessorKey: (row: any) => {
        const tenantType = row.tenants?.tenant_type;
        const name = tenantType === 'professional' && row.tenants?.company_name
          ? row.tenants.company_name
          : `${row.tenants?.first_name || ''} ${row.tenants?.last_name || ''}`.trim();
        return (
          <div>
            <div className="font-medium">{name}</div>
            <div className="text-xs text-muted-foreground">
              {tenantType === 'professional' ? 'Professionnel' : 'Particulier'}
            </div>
          </div>
        );
      },
    },
    {
      header: 'Loyer + Charges',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-1">
          <Euro className="h-3 w-3 text-muted-foreground" />
          <span>{row.rent_amount?.toFixed(2)} + {row.charges_amount?.toFixed(2)}</span>
          {row.billing_frequency === 'quarterly' && (
            <span className="ml-1 text-xs bg-blue-100 text-blue-700 px-1.5 py-0.5 rounded" title={`Mois: ${(row.quarterly_months || [1,4,7,10]).join(', ')}`}>
              Trim.
            </span>
          )}
        </div>
      ),
    },
    {
      header: 'TVA',
      accessorKey: (row: any) => (
        <div className="flex items-center gap-1">
          <Percent className="h-3 w-3 text-muted-foreground" />
          <span>{row.rent_lines?.length ? 'Multi-taux' : `${row.vat_rate || 0}%`}</span>
        </div>
      ),
    },
    {
      header: 'Échéance',
      accessorKey: (row: any) => (
        <span className="text-sm">Le {row.payment_day} du mois</span>
      ),
    },
    {
      header: 'Statut',
      accessorKey: (row: any) => <StatusBadge status={row.status} />,
    },
    {
      header: 'Actions',
      accessorKey: (row: any) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8">
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="bg-popover">
            <DropdownMenuItem onClick={() => setSelectedContract(row)} className="cursor-pointer">
              <Eye className="mr-2 h-4 w-4" />
              Détails
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => {
              setEditingContract(row);
              setSelectedPropertyId(row.property_id);
              setPaymentAccountId(row.payment_account_id || '');
              setEditBillingFrequency(row.billing_frequency || 'monthly');
              setEditQuarterlyMonths(row.quarterly_months || [1, 4, 7, 10]);
              setIsEditOpen(true);
            }} className="cursor-pointer">
              <Edit className="mr-2 h-4 w-4" />
              Modifier
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            {row.status === 'active' && (
              <DropdownMenuItem
                onClick={() => updateContractStatus.mutate({ id: row.id, status: 'paused' })}
                className="cursor-pointer"
              >
                <Pause className="mr-2 h-4 w-4" />
                Suspendre le contrat
              </DropdownMenuItem>
            )}
            {row.status === 'paused' && (
              <DropdownMenuItem
                onClick={() => updateContractStatus.mutate({ id: row.id, status: 'active' })}
                className="cursor-pointer"
              >
                <Play className="mr-2 h-4 w-4" />
                Réactiver le contrat
              </DropdownMenuItem>
            )}
            {row.status !== 'ended' && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => updateContractStatus.mutate({
                    id: row.id,
                    status: 'ended',
                    endDate: new Date().toISOString().split('T')[0]
                  })}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <XCircle className="mr-2 h-4 w-4" />
                  Mettre fin au contrat
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <DropdownMenuItem
                  onSelect={(e) => e.preventDefault()}
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <Trash2 className="mr-2 h-4 w-4" />
                  Supprimer le contrat
                </DropdownMenuItem>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>Supprimer le contrat</AlertDialogTitle>
                  <AlertDialogDescription>
                    Êtes-vous sûr de vouloir supprimer ce contrat ? Cette action est irréversible et supprimera également toutes les périodes de loyer et paiements associés.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Annuler</AlertDialogCancel>
                  <AlertDialogAction
                    onClick={() => deleteContract.mutate(row.id)}
                    disabled={deleteContract.isPending}
                    className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  >
                    {deleteContract.isPending ? 'Suppression...' : 'Supprimer'}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  return (
    <AppLayout title="Contrats" description="Gérez vos contrats de location">
      <PageHeader
        title="Contrats"
        description={`${filteredContracts.length} contrat${filteredContracts.length > 1 ? 's' : ''}${hasActiveFilters ? ' (filtrés)' : ''}`}
        actions={
          <div className="flex gap-2">
            <ImportContractsDialog />
            <Dialog open={isOpen} onOpenChange={(open) => {
              setIsOpen(open);
              if (!open) {
                setSelectedPropertyId('');
                setPaymentAccountId('');
                setFirstLineLabel('');
                setExtraLines([]);
              }
            }}>
              <DialogTrigger asChild>
                <Button><Plus className="mr-2 h-4 w-4" /> Nouveau contrat</Button>
              </DialogTrigger>
            <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Nouveau contrat de location</DialogTitle>
              </DialogHeader>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  createContract.mutate(new FormData(e.currentTarget));
                }}
                className="space-y-4"
              >
                {/* Property Selection */}
                <div className="space-y-2">
                  <Label>Bien *</Label>
                  <Select 
                    name="property_id" 
                    required 
                    value={selectedPropertyId}
                    onValueChange={(id) => { setSelectedPropertyId(id); setPaymentAccountId(''); }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un bien" />
                    </SelectTrigger>
                    <SelectContent>
                      {properties.map((p: any) => (
                        <SelectItem key={p.id} value={p.id}>
                          {p.name || p.address} {p.scis?.name && `(${p.scis.name})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Property Info */}
                {selectedProperty && (
                  <div className="bg-muted rounded-lg p-3 text-sm">
                    <div className="font-medium">{(selectedProperty as any).name || (selectedProperty as any).address}</div>
                    <div className="text-muted-foreground">
                      SCI: {(selectedProperty as any).scis?.name || '-'}
                    </div>
                  </div>
                )}

                <PaymentAccountSelect sciId={(selectedProperty as any)?.scis?.id}
                  value={paymentAccountId} onChange={setPaymentAccountId} />

                {/* Tenant Selection */}
                <div className="space-y-2">
                  <Label>Locataire *</Label>
                  <Select name="tenant_id" required>
                    <SelectTrigger>
                      <SelectValue placeholder="Sélectionner un locataire" />
                    </SelectTrigger>
                    <SelectContent>
                      {tenants.map((t: any) => (
                        <SelectItem key={t.id} value={t.id}>
                          {t.tenant_type === 'professional' && t.company_name 
                            ? `${t.company_name} (${t.first_name} ${t.last_name})`
                            : `${t.first_name} ${t.last_name}`
                          }
                          {t.tenant_type === 'professional' && ' - Pro'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Date d'entrée */}
                <div className="space-y-2">
                  <Label htmlFor="start_date">Date d'entrée dans les lieux *</Label>
                  <Input id="start_date" name="start_date" type="date" required />
                </div>

                {/* Amounts */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="rent_amount">Loyer (€) *</Label>
                    <Input 
                      id="rent_amount" 
                      name="rent_amount" 
                      type="number" 
                      step="0.01" 
                      required 
                      placeholder="800.00"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="charges_amount">Charges (€)</Label>
                    <Input 
                      id="charges_amount" 
                      name="charges_amount" 
                      type="number" 
                      step="0.01" 
                      defaultValue="0" 
                      placeholder="50.00"
                    />
                  </div>
                </div>

                <RentLinesEditor
                  firstLabel={firstLineLabel}
                  onFirstLabelChange={setFirstLineLabel}
                  lines={extraLines}
                  onChange={setExtraLines}
                />

                {/* Deposit Amount */}
                <div className="space-y-2">
                  <Label htmlFor="deposit_amount">Dépôt de garantie (€)</Label>
                  <Input 
                    id="deposit_amount" 
                    name="deposit_amount" 
                    type="number" 
                    step="0.01" 
                    defaultValue="0" 
                    placeholder="800.00"
                  />
                  <p className="text-xs text-muted-foreground">Montant du dépôt de garantie (exonéré de TVA). Laissez à 0 si non applicable.</p>
                </div>

                {/* Billing Frequency */}
                <div className="space-y-2">
                  <Label>Périodicité de facturation</Label>
                  <Select 
                    value={billingFrequency} 
                    onValueChange={(value: 'monthly' | 'quarterly') => setBillingFrequency(value)}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Mensuel</SelectItem>
                      <SelectItem value="quarterly">Trimestriel</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Quarterly months selector - only show when quarterly is selected */}
                {billingFrequency === 'quarterly' && (
                  <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                    <Label>Mois de facturation trimestrielle</Label>
                    <p className="text-xs text-muted-foreground mb-2">
                      Sélectionnez les 4 mois où les appels de loyer seront générés
                    </p>
                    <div className="grid grid-cols-4 gap-2">
                      {[0, 1, 2, 3].map((index) => (
                        <Select
                          key={index}
                          value={quarterlyMonths[index]?.toString() || '1'}
                          onValueChange={(value) => {
                            const newMonths = [...quarterlyMonths];
                            newMonths[index] = parseInt(value);
                            setQuarterlyMonths(newMonths);
                          }}
                        >
                          <SelectTrigger className="text-xs">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {monthNames.map((name, i) => (
                              <SelectItem key={i + 1} value={(i + 1).toString()}>
                                {name.substring(0, 3)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ))}
                    </div>
                  </div>
                )}

                {/* VAT and Payment Day */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Régime de TVA</Label>
                    <Select name="vat_rate" defaultValue="0">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">Sans TVA (0%)</SelectItem>
                        <SelectItem value="20">TVA 20%</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="payment_day">Jour d'échéance mensuelle</Label>
                    <Select name="payment_day" defaultValue="5">
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                          <SelectItem key={day} value={day.toString()}>
                            Le {day} du mois
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button type="submit" className="w-full" disabled={createContract.isPending}>
                  {createContract.isPending ? 'Création...' : 'Créer le contrat'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
          </div>
        }
      />

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4 p-4 bg-muted/50 rounded-lg flex-wrap">
        <Filter className="h-4 w-4 text-muted-foreground" />

        <div className="relative flex-1 max-w-xs">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher (bien, locataire, SCI...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-8"
          />
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">SCI:</Label>
          <Select value={filterSciId} onValueChange={setFilterSciId}>
            <SelectTrigger className="w-40">
              <SelectValue placeholder="Toutes" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Toutes les SCI</SelectItem>
              {scis.map((sci) => (
                <SelectItem key={sci.id} value={sci.id}>{sci.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Bien:</Label>
          <Select value={filterPropertyId} onValueChange={setFilterPropertyId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les biens</SelectItem>
              {properties.map((p: any) => (
                <SelectItem key={p.id} value={p.id}>{p.name || p.address}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        <div className="flex items-center gap-2">
          <Label className="text-sm whitespace-nowrap">Locataire:</Label>
          <Select value={filterTenantId} onValueChange={setFilterTenantId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Tous" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Tous les locataires</SelectItem>
              {tenants.map((t: any) => (
                <SelectItem key={t.id} value={t.id}>
                  {t.tenant_type === 'professional' && t.company_name 
                    ? t.company_name 
                    : `${t.first_name} ${t.last_name}`}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {hasActiveFilters && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => {
              setFilterSciId('all');
              setFilterPropertyId('all');
              setFilterTenantId('all');
              setSearchTerm('');
            }}
          >
            <X className="h-4 w-4 mr-1" /> Réinitialiser
          </Button>
        )}
      </div>

      <DataTable
        columns={columns}
        data={filteredContracts}
        isLoading={isLoading}
        emptyMessage="Aucun contrat. Créez d'abord des biens et locataires."
        selectable
        selectedIds={selectedIds}
        onSelectionChange={setSelectedIds}
      />

      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 bg-background border rounded-lg shadow-lg px-4 py-3 flex items-center gap-4">
          <span className="text-sm font-medium">{selectedIds.size} sélectionné(s)</span>
          <AlertDialog open={bulkDeleteOpen} onOpenChange={(open) => !bulkDeleteLoading && setBulkDeleteOpen(open)}>
            <AlertDialogTrigger asChild>
              <Button variant="destructive" size="sm"><Trash2 className="h-4 w-4 mr-2" /> Supprimer</Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Supprimer {selectedIds.size} contrat(s) ?</AlertDialogTitle>
                <AlertDialogDescription asChild>
                  <div className="space-y-2">
                    <p>Cette action est <strong>irréversible</strong>.</p>
                    <p className="text-destructive font-medium">⚠️ Toutes les données liées seront supprimées :</p>
                    <ul className="list-disc list-inside text-sm space-y-1">
                      <li>Périodes de loyer associées</li>
                      <li>Paiements et allocations</li>
                      <li>Quittances et appels de fonds</li>
                    </ul>
                    <p className="text-sm text-muted-foreground">Les biens et locataires ne seront pas affectés.</p>
                  </div>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={bulkDeleteLoading}>Annuler</AlertDialogCancel>
                <AlertDialogAction
                  className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                  disabled={bulkDeleteLoading}
                  onClick={async () => {
                    setBulkDeleteLoading(true);
                    let successCount = 0;
                    const totalCount = selectedIds.size;
                    try {
                      for (const id of selectedIds) {
                        try {
                          const { error } = await supabase.from('leases').delete().eq('id', id);
                          if (error) throw error;
                          successCount++;
                        } catch (err: any) {
                          console.error(`Échec suppression contrat ${id}:`, err);
                          toast.error(`Erreur sur un contrat : ${err.message || 'Erreur inconnue'}`);
                        }
                      }
                      queryClient.invalidateQueries({ queryKey: ['leases'] });
                      queryClient.invalidateQueries({ queryKey: ['receipts'] });
                      queryClient.invalidateQueries({ queryKey: ['call_for_funds'] });
                      queryClient.invalidateQueries({ queryKey: ['payments'] });
                      queryClient.invalidateQueries({ queryKey: ['rent_periods'] });
                      if (successCount === totalCount) {
                        toast.success(`${successCount} contrat(s) supprimé(s)`);
                      } else if (successCount > 0) {
                        toast.warning(`${successCount}/${totalCount} contrat(s) supprimé(s). ${totalCount - successCount} erreur(s).`);
                      }
                      setSelectedIds(new Set());
                    } finally {
                      setBulkDeleteLoading(false);
                      setBulkDeleteOpen(false);
                    }
                  }}
                >
                  {bulkDeleteLoading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Suppression...</> : 'Supprimer'}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
          <Button variant="outline" size="sm" onClick={() => setSelectedIds(new Set())} disabled={bulkDeleteLoading}>Annuler</Button>
        </div>
      )}

      {/* Details Sheet */}
      <Sheet open={!!selectedContract} onOpenChange={() => setSelectedContract(null)}>
        <SheetContent className="w-full sm:max-w-xl overflow-y-auto">
          {selectedContract && (
            <>
              <SheetHeader>
                <SheetTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5" />
                  Contrat - {selectedContract.properties?.name || selectedContract.properties?.address}
                </SheetTitle>
              </SheetHeader>

              <Tabs defaultValue="info" className="mt-6">
                <TabsList className="grid w-full grid-cols-2">
                  <TabsTrigger value="info">Infos</TabsTrigger>
                  <TabsTrigger value="details">Détails financiers</TabsTrigger>
                </TabsList>

                <TabsContent value="info" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Bien et Locataire</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Bien</p>
                        <p className="font-medium">{selectedContract.properties?.name || selectedContract.properties?.address}</p>
                        <p className="text-sm text-muted-foreground">SCI: {selectedContract.properties?.scis?.name}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Locataire</p>
                        <p className="font-medium">
                          {selectedContract.tenants?.tenant_type === 'professional' && selectedContract.tenants?.company_name
                            ? selectedContract.tenants.company_name
                            : `${selectedContract.tenants?.first_name || ''} ${selectedContract.tenants?.last_name || ''}`.trim()}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {selectedContract.tenants?.tenant_type === 'professional' ? 'Professionnel' : 'Particulier'}
                        </p>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Dates</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div>
                        <p className="text-sm text-muted-foreground">Entrée dans les lieux</p>
                        <p className="font-medium flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          {format(new Date(selectedContract.start_date), 'dd MMMM yyyy', { locale: fr })}
                        </p>
                      </div>
                      {selectedContract.end_date && (
                        <div>
                          <p className="text-sm text-muted-foreground">Date de fin</p>
                          <p className="font-medium flex items-center gap-1">
                            <Calendar className="h-4 w-4" />
                            {format(new Date(selectedContract.end_date), 'dd MMMM yyyy', { locale: fr })}
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Statut</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <StatusBadge status={selectedContract.status} />
                    </CardContent>
                  </Card>

                  <Button
                    onClick={() => {
                      setEditingContract(selectedContract);
                      setSelectedPropertyId(selectedContract.property_id);
                      setPaymentAccountId(selectedContract.payment_account_id || '');
                      setIsEditOpen(true);
                    }}
                    className="w-full"
                  >
                    <Edit className="mr-2 h-4 w-4" />
                    Modifier
                  </Button>
                </TabsContent>

                <TabsContent value="details" className="space-y-4 mt-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Montants</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">Loyer mensuel</p>
                          <p className="font-medium flex items-center gap-1">
                            <Euro className="h-4 w-4" />
                            {selectedContract.rent_amount?.toFixed(2)} €
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Charges</p>
                          <p className="font-medium flex items-center gap-1">
                            <Euro className="h-4 w-4" />
                            {selectedContract.charges_amount?.toFixed(2)} €
                          </p>
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <p className="text-sm text-muted-foreground">TVA</p>
                          <p className="font-medium flex items-center gap-1">
                            <Percent className="h-4 w-4" />
                            {selectedContract.vat_rate || 0}%
                          </p>
                        </div>
                        <div>
                          <p className="text-sm text-muted-foreground">Jour d'échéance</p>
                          <p className="font-medium">Le {selectedContract.payment_day} du mois</p>
                        </div>
                      </div>
                      {Array.isArray(selectedContract.rent_lines) && selectedContract.rent_lines.length > 0 && (
                        <div className="border-t pt-3 space-y-1">
                          <p className="text-sm text-muted-foreground">Lignes de loyer</p>
                          {selectedContract.rent_lines.map((l: any, i: number) => (
                            <div key={i} className="flex justify-between text-sm">
                              <span>{l.label || `Ligne ${i + 1}`}</span>
                              <span>
                                {Number(l.rent_ht || 0).toFixed(2)} € + {Number(l.charges_ht || 0).toFixed(2)} € · TVA {l.vat_rate || 0}%
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="border-t pt-3">
                        <p className="text-sm text-muted-foreground">Compte bancaire</p>
                        <p className="font-medium">{selectedContract.sci_payment_accounts
                          ? `${selectedContract.sci_payment_accounts.label} ···· ${selectedContract.sci_payment_accounts.iban.slice(-4)}`
                          : 'Aucun compte associé'}</p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </>
          )}
        </SheetContent>
      </Sheet>

      {/* Edit Dialog */}
      <Dialog open={isEditOpen} onOpenChange={(open) => {
        setIsEditOpen(open);
        if (!open) {
          setEditingContract(null);
          setSelectedPropertyId('');
          setPaymentAccountId('');
        }
      }}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Modifier le contrat</DialogTitle>
          </DialogHeader>
          {editingContract && (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                updateContract.mutate(new FormData(e.currentTarget));
              }}
              className="space-y-4"
            >
              {/* Property Selection */}
              <div className="space-y-2">
                <Label>Bien *</Label>
                <Select
                  name="property_id"
                  required
                  value={selectedPropertyId}
                  onValueChange={(id) => { setSelectedPropertyId(id); setPaymentAccountId(''); }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un bien" />
                  </SelectTrigger>
                  <SelectContent>
                    {properties.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name || p.address} {p.scis?.name && `(${p.scis.name})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Property Info */}
              {selectedProperty && (
                <div className="bg-muted rounded-lg p-3 text-sm">
                  <div className="font-medium">{(selectedProperty as any).name || (selectedProperty as any).address}</div>
                  <div className="text-muted-foreground">
                    SCI: {(selectedProperty as any).scis?.name || '-'}
                  </div>
                </div>
              )}

              <PaymentAccountSelect sciId={(selectedProperty as any)?.scis?.id}
                value={paymentAccountId} onChange={setPaymentAccountId} />

              {/* Tenant Selection */}
              <div className="space-y-2">
                <Label>Locataire *</Label>
                <Select name="tenant_id" required defaultValue={editingContract.tenant_id}>
                  <SelectTrigger>
                    <SelectValue placeholder="Sélectionner un locataire" />
                  </SelectTrigger>
                  <SelectContent>
                    {tenants.map((t: any) => (
                      <SelectItem key={t.id} value={t.id}>
                        {t.tenant_type === 'professional' && t.company_name
                          ? `${t.company_name} (${t.first_name} ${t.last_name})`
                          : `${t.first_name} ${t.last_name}`
                        }
                        {t.tenant_type === 'professional' && ' - Pro'}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Dates */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-start_date">Date d'entrée *</Label>
                  <Input
                    id="edit-start_date"
                    name="start_date"
                    type="date"
                    required
                    defaultValue={editingContract.start_date}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-end_date">Date de fin</Label>
                  <Input
                    id="edit-end_date"
                    name="end_date"
                    type="date"
                    defaultValue={editingContract.end_date || ''}
                  />
                </div>
              </div>

              {/* Amounts */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-rent_amount">Loyer (€) *</Label>
                  <Input
                    id="edit-rent_amount"
                    name="rent_amount"
                    type="number"
                    step="0.01"
                    required
                    defaultValue={editFirstLine ? editFirstLine.rent_ht : editingContract.rent_amount}
                    placeholder="800.00"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-charges_amount">Charges (€)</Label>
                  <Input
                    id="edit-charges_amount"
                    name="charges_amount"
                    type="number"
                    step="0.01"
                    defaultValue={editFirstLine ? editFirstLine.charges_ht : (editingContract.charges_amount || 0)}
                    placeholder="50.00"
                  />
                </div>
              </div>

              <RentLinesEditor
                firstLabel={editFirstLineLabel}
                onFirstLabelChange={setEditFirstLineLabel}
                lines={editExtraLines}
                onChange={setEditExtraLines}
              />

              {/* Deposit Amount */}
              <div className="space-y-2">
                <Label htmlFor="edit-deposit_amount">Dépôt de garantie (€)</Label>
                <Input
                  id="edit-deposit_amount"
                  name="deposit_amount"
                  type="number"
                  step="0.01"
                  defaultValue={editingContract.deposit_amount || 0}
                  placeholder="800.00"
                />
                <p className="text-xs text-muted-foreground">Montant du dépôt de garantie (exonéré de TVA). Laissez à 0 si non applicable.</p>
              </div>

              <IndexationSettings key={editingContract.id} lease={editingContract} />

              {/* Billing Frequency */}
              <div className="space-y-2">
                <Label>Périodicité de facturation</Label>
                <Select 
                  value={editBillingFrequency} 
                  onValueChange={(value: 'monthly' | 'quarterly') => setEditBillingFrequency(value)}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="monthly">Mensuel</SelectItem>
                    <SelectItem value="quarterly">Trimestriel</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Quarterly months selector - only show when quarterly is selected */}
              {editBillingFrequency === 'quarterly' && (
                <div className="space-y-2 p-3 border rounded-lg bg-muted/30">
                  <Label>Mois de facturation trimestrielle</Label>
                  <p className="text-xs text-muted-foreground mb-2">
                    Sélectionnez les 4 mois où les appels de loyer seront générés
                  </p>
                  <div className="grid grid-cols-4 gap-2">
                    {[0, 1, 2, 3].map((index) => (
                      <Select
                        key={index}
                        value={editQuarterlyMonths[index]?.toString() || '1'}
                        onValueChange={(value) => {
                          const newMonths = [...editQuarterlyMonths];
                          newMonths[index] = parseInt(value);
                          setEditQuarterlyMonths(newMonths);
                        }}
                      >
                        <SelectTrigger className="text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {monthNames.map((name, i) => (
                            <SelectItem key={i + 1} value={(i + 1).toString()}>
                              {name.substring(0, 3)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ))}
                  </div>
                </div>
              )}

              {/* VAT and Payment Day */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Régime de TVA</Label>
                  <Select name="vat_rate" defaultValue={(editFirstLine ? editFirstLine.vat_rate : editingContract.vat_rate)?.toString() || '0'}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="0">Sans TVA (0%)</SelectItem>
                      <SelectItem value="20">TVA 20%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-payment_day">Jour d'échéance mensuelle</Label>
                  <Select name="payment_day" defaultValue={editingContract.payment_day?.toString() || '5'}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                        <SelectItem key={day} value={day.toString()}>
                          Le {day} du mois
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="flex-1"
                  onClick={() => {
                    setIsEditOpen(false);
                    setEditingContract(null);
                    setSelectedPropertyId('');
                    setPaymentAccountId('');
                  }}
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  className="flex-1"
                  disabled={updateContract.isPending}
                >
                  {updateContract.isPending ? 'Mise à jour...' : 'Mettre à jour'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </AppLayout>
  );
}
